import * as React from "react";
import { Box, CircularProgress, Modal, Stack, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import moment from "moment";
import { getHeaders } from "../../../utils/HeaderHelper";

type HistoryChange = {
  source_column_name: string;
  old_value_text: string | null;
  new_value_text: string | null;
  old_value_display: string | null;
  new_value_display: string | null;
};

type HistoryEntry = {
  action_group_id: string;
  action_type: "create" | "update" | "archive" | "delete";
  changed_at: string;
  actor: string;
  snapshot?: Record<string, unknown> | null;
  changes: HistoryChange[];
};

interface Props {
  open: boolean;
  interventionId: number | null;
  onClose: () => void;
  csrfToken: string;
}

const FIELD_ORDER = [
  "Date",
  "Type",
  "Activity",
  "Related Condition/Dx",
  "Medication Involved",
  "Annualized Hard Savings",
  "Annualized Hard Savings Amount",
  "Needs Supervisor Support",
  "Response Received",
  "Response Status",
  "Intervention Signed",
];

const headerForAction = (actionType: string) => {
  switch (actionType) {
    case "create":
      return "Intervention Created";
    case "update":
      return "Intervention Updated";
    case "archive":
      return "Intervention Archived";
    case "delete":
      return "Intervention Deleted";
    default:
      return "Intervention Updated";
  }
};

const toBlank = (value: string | null | undefined) =>
  value == null || `${value}`.trim() === "" ? "(blank)" : `${value}`;

const stringifyPrimitive = (value: unknown) => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return "";
};

const formatDateValue = (value: unknown) => {
  const strValue = stringifyPrimitive(value);
  if (!strValue) return "(blank)";
  if (/^\d{4}-\d{2}-\d{2}$/.test(strValue)) {
    return moment(strValue, "YYYY-MM-DD", true).format("MM/DD/YYYY");
  }
  
  const asUtc = moment.utc(strValue);
  if (asUtc.isValid()) {
    return asUtc.format("MM/DD/YYYY");
  }
  return strValue;
};

const formatCurrency = (value: unknown) => {
  const primitiveValue = stringifyPrimitive(value);
  if (!primitiveValue) return "(blank)";
  const numeric = Number(primitiveValue);
  if (Number.isNaN(numeric)) return primitiveValue;
  return `$${numeric.toFixed(2)}`;
};

const formatChecked = (value: unknown) => {
  if (value === true || value === "true" || value === "Yes") return "Checked";
  return "(blank)";
};

const normalizeResponseStatus = (value: unknown) => {
  const primitiveValue = stringifyPrimitive(value);
  if (!primitiveValue) return "(blank)";
  return primitiveValue.replace(/^Recommendation\s+/i, "");
};

const buildConditionDisplay = (code: unknown, description: unknown) => {
  const codeText = stringifyPrimitive(code).trim();
  const descriptionText = stringifyPrimitive(description).trim();
  if (!codeText && !descriptionText) return "(blank)";
  if (codeText && descriptionText) return `${codeText} - ${descriptionText}`;
  return codeText || descriptionText;
};

const rowLabelSx = {
  color: "#1E1E1E",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "18px",
};

const cardBorderForAction = (actionType: HistoryEntry["action_type"]) => {
  switch (actionType) {
    case "archive":
    case "delete":
      return "#FF0000";
    case "create":
      return "#07BC2A";
    case "update":
    default:
      return "#FFDC68";
  }
};

const InterventionHistoryModal: React.FC<Props> = ({
  open,
  interventionId,
  onClose,
  csrfToken,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [entries, setEntries] = React.useState<HistoryEntry[]>([]);

  React.useEffect(() => {
    if (!open || !interventionId) return;

    setLoading(true);
    fetch(`/interventions/${interventionId}/history`, {
      method: "GET",
      headers: getHeaders(csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        const resource: HistoryEntry[] = Array.isArray(result?.resource)
          ? result.resource
          : [];
        const groupedByAction = new Map<string, HistoryEntry>();
        resource.forEach((entry) => {
          const existing = groupedByAction.get(entry.action_group_id);
          if (!existing) {
            groupedByAction.set(entry.action_group_id, {
              ...entry,
              changes: [...(entry.changes || [])],
            });
            return;
          }

          // Keep all changed fields for the same action group in one timeline entry.
          groupedByAction.set(entry.action_group_id, {
            ...existing,
            changes: [...existing.changes, ...(entry.changes || [])],
            snapshot: existing.snapshot || entry.snapshot,
          });
        });

        const normalized = Array.from(groupedByAction.values()).sort(
          (a, b) =>
            moment(b.changed_at).valueOf() - moment(a.changed_at).valueOf(),
        );
        setEntries(normalized);
      })
      .catch(() => {
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, [open, interventionId, csrfToken]);

  const buildCreateRows = (
    snapshot: Record<string, unknown> | null | undefined,
  ) => {
    const data = snapshot || {};
    return [
      ["Date", "(blank)", formatDateValue(data.occurred_on_date)],
      ["Type", "(blank)", toBlank(data.intervention_type as string)],
      ["Activity", "(blank)", toBlank(data.activity as string)],
      [
        "Related Condition/Dx",
        "(blank)",
        buildConditionDisplay(
          data.related_condition_code,
          data.related_condition_description,
        ),
      ],
      [
        "Medication Involved",
        "(blank)",
        toBlank(data.related_medication as string),
      ],
      ["Annualized Hard Savings", "(blank)", formatChecked(data.has_savings)],
      [
        "Annualized Hard Savings Amount",
        "(blank)",
        formatCurrency(data.saving_amounts),
      ],
      [
        "Needs Supervisor Support",
        "(blank)",
        formatChecked(data.is_ready_for_review),
      ],
      [
        "Response Received",
        "(blank)",
        toBlank(normalizeResponseStatus(data.has_prescriber_response)) ===
        "(blank)"
          ? "(blank)"
          : "Checked",
      ],
      [
        "Response Status",
        "(blank)",
        normalizeResponseStatus(data.has_prescriber_response),
      ],
      [
        "Intervention Signed",
        "(blank)",
        data.status === "signed" ? "Signed" : "(blank)",
      ],
    ] as [string, string, string][];
  };

  const buildUpdateRows = (changes: HistoryChange[]) => {
    const byField = new Map(
      changes.map((change) => [change.source_column_name, change]),
    );
    const rows: [string, string, string][] = [];

    const addSimpleChange = (
      sourceColumn: string,
      label: string,
      formatter?: (v: unknown) => string,
    ) => {
      const change = byField.get(sourceColumn);
      if (!change) return;
      const oldValue = formatter
        ? formatter(change.old_value_text)
        : toBlank(change.old_value_display || change.old_value_text);
      const newValue = formatter
        ? formatter(change.new_value_text)
        : toBlank(change.new_value_display || change.new_value_text);
      rows.push([label, oldValue, newValue]);
    };

    addSimpleChange("occurred_on_date", "Date", formatDateValue);
    addSimpleChange("intervention_type", "Type");
    addSimpleChange("activity", "Activity");

    const conditionCode = byField.get("related_condition_code");
    const conditionDescription = byField.get("related_condition_description");
    if (conditionCode || conditionDescription) {
      rows.push([
        "Related Condition/Dx",
        buildConditionDisplay(
          conditionCode?.old_value_text,
          conditionDescription?.old_value_text,
        ),
        buildConditionDisplay(
          conditionCode?.new_value_text,
          conditionDescription?.new_value_text,
        ),
      ]);
    }

    addSimpleChange("related_medication", "Medication Involved");
    addSimpleChange("has_savings", "Annualized Hard Savings", formatChecked);
    addSimpleChange(
      "saving_amounts",
      "Annualized Hard Savings Amount",
      formatCurrency,
    );
    addSimpleChange(
      "is_ready_for_review",
      "Needs Supervisor Support",
      formatChecked,
    );

    const responseChange = byField.get("has_prescriber_response");
    if (responseChange) {
      rows.push(
        [
          "Response Received",
          toBlank(normalizeResponseStatus(responseChange.old_value_text)) ===
          "(blank)"
            ? "(blank)"
            : "Checked",
          toBlank(normalizeResponseStatus(responseChange.new_value_text)) ===
          "(blank)"
            ? "(blank)"
            : "Checked",
        ],
        [
          "Response Status",
          normalizeResponseStatus(responseChange.old_value_text),
          normalizeResponseStatus(responseChange.new_value_text),
        ],
      );
    }

    const statusChange = byField.get("status");
    if (
      statusChange &&
      (statusChange.old_value_text === "signed" ||
        statusChange.new_value_text === "signed")
    ) {
      rows.push([
        "Intervention Signed",
        statusChange.old_value_text === "signed" ? "Signed" : "(blank)",
        statusChange.new_value_text === "signed" ? "Signed" : "(blank)",
      ]);
    }

    return rows.sort(
      (a, b) => FIELD_ORDER.indexOf(a[0]) - FIELD_ORDER.indexOf(b[0]),
    );
  };

  const buildRowsForEntry = (entry: HistoryEntry) => {
    if (entry.action_type === "create") {
      return buildCreateRows(entry.snapshot || {});
    }
    if (entry.action_type === "update") {
      return buildUpdateRows(entry.changes || []);
    }
    if (entry.action_type === "archive") {
      return [["Intervention was archived", "", ""]];
    }
    if (entry.action_type === "delete") {
      return [["Intervention was deleted", "", ""]];
    }
    return [];
  };

  return (
    <Modal
      open={open}
      className="interventions-history-modal"
      onClose={onClose}
    >
      <div
        className="paper"
        style={{
          maxWidth: "920px",
          height: "88vh",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div className="paperInner">
          <Box>
            <Box className="main-header-container">
              <div
                className="main-header"
                style={{
                  marginBottom: "0px",
                  borderBottom: "1px solid #E7E7E7",
                }}
              >
                <span>Intervention Log</span>
                <button
                  id="dismiss-button"
                  onClick={onClose}
                  type="button"
                  style={{
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                  }}
                >
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </button>
              </div>
            </Box>
            <div className="modal-content">
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!loading && entries.length === 0 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    color: "#666",
                  }}
                >
                  No intervention history found.
                </Typography>
              )}
              {!loading && entries.length > 0 && (
                <Stack
                  spacing={1}
                  sx={{ maxHeight: "74vh", overflowY: "auto" }}
                >
                  {entries.map((entry) => {
                    const rows = buildRowsForEntry(entry);
                    return (
                      <Box
                        key={entry.action_group_id}
                        className="tile-container"
                      >
                        <Box
                          sx={{
                            width: "2px",
                            borderLeft: "2px solid",
                            borderColor: cardBorderForAction(entry.action_type),
                          }}
                        ></Box>
                        <Box>
                          <Typography className="tile-heading">
                            {headerForAction(entry.action_type)}
                          </Typography>
                          <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ color: "#8D8D8D" }}
                            >
                              <EventNoteIcon
                                sx={{ fontSize: 14 }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: "QuicksandMedium",
                                  color: "#8D8D8D",
                                  fontSize: "12px",
                                }}
                              >
                                {moment(entry.changed_at).format(
                                  "MMMM D, YYYY",
                                )}{" "}
                                • {moment(entry.changed_at).format("h:mmA")}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ color: "#8D8D8D" }}
                            >
                              <PersonIcon
                                sx={{ fontSize: 14 }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: "QuicksandMedium",
                                  color: "#8D8D8D",
                                  fontSize: "12px",
                                }}
                              >
                                {entry.actor}
                              </Typography>
                            </Stack>
                          </Stack>

                          {entry.action_type === "archive" ? (
                            <Typography sx={rowLabelSx}>
                              {rows[0]?.[0]}
                            </Typography>
                          ) : (
                            <Stack
                              component="ul"
                              sx={{ margin: 0, paddingLeft: "20px" }}
                              spacing={0.3}
                            >
                              {rows.map((row, index) => (
                                <Box
                                  component="li"
                                  key={`${entry.action_group_id}-${index}`}
                                >
                                  {row[1] === "" && row[2] === "" ? (
                                    <Typography sx={rowLabelSx}>
                                      {row[0]}
                                    </Typography>
                                  ) : (
                                    <Typography sx={rowLabelSx}>
                                      <Box component="span" sx={rowLabelSx}>
                                        {row[0]}:
                                      </Box>{" "}
                                      {row[1]} {"→"} {row[2]}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </div>
          </Box>
        </div>
      </div>
    </Modal>
  );
};

export default InterventionHistoryModal;
